/**
 * CredentialMention — inline node Tiptap cu chip „🔑 title 👁".
 *
 * Extension pur — fără Suggestion wrapper. Inserarea se face de către
 * SlashMenu (vezi SlashMenu.tsx) via `editor.chain().insertContent(...)`.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Node, mergeAttributes } from '@tiptap/core';
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';

import { credentialsApi } from '@/api/credentials';
import { decryptCredential, type CredentialPayload } from '@/crypto/sodium';
import { useAuthStore } from '@/stores/auth';

export interface CredentialMentionAttrs {
  id: string;
  title: string;
}

export const CredentialMentionNode = Node.create({
  name: 'credentialMention',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      id: { default: '' },
      title: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-cred-mention]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes({ 'data-cred-mention': '' }, HTMLAttributes),
      `cred:${HTMLAttributes.title}`,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(CredentialMentionView);
  },
});

function CredentialMentionView({ node }: NodeViewProps) {
  const { t } = useTranslation();
  const id = node.attrs.id as string;
  const title = node.attrs.title as string;
  const keyPair = useAuthStore((s) => s.keyPair);

  const [plain, setPlain] = useState<CredentialPayload | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as globalThis.Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const reveal = async () => {
    if (!keyPair) {
      setErr(t('notes_blocks.cred_mention.no_session_key'));
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const detail = await credentialsApi.get(id);
      const decoded = await decryptCredential(
        { ciphertext: detail.ciphertext, nonce: detail.nonce, dek_wrapped: detail.dek_wrapped },
        keyPair,
      );
      setPlain(decoded);
      setOpen(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('notes_blocks.cred_mention.error'));
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <NodeViewWrapper
      as="span"
      contentEditable={false}
      draggable
      data-drag-handle
      className="inline-flex items-center gap-1 align-baseline"
    >
      <span
        ref={ref}
        className="relative inline-flex items-center gap-1 bg-brand-50 border border-brand-200 rounded px-1.5 py-0.5 text-xs text-brand-700 font-mono dark:bg-brand-900/40 dark:border-brand-800 dark:text-brand-300"
      >
        <span>🔑 {title}</span>
        <button
          type="button"
          onClick={reveal}
          disabled={busy}
          className="text-brand-600 hover:text-brand-800 dark:text-brand-300 dark:hover:text-brand-200 disabled:opacity-50"
          title={t('notes_blocks.cred_mention.reveal')}
        >
          {busy ? '…' : '👁'}
        </button>
        {open && (
          <span
            className="absolute left-0 top-full mt-1 z-50 bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded-md shadow-lg p-3 min-w-[260px] text-left"
            style={{ userSelect: 'text' }}
          >
            {err ? (
              <span className="text-xs text-red-600 dark:text-red-400">{err}</span>
            ) : plain ? (
              <span className="block space-y-1 text-[11px] text-[var(--text)]">
                {Object.entries(plain)
                  .filter(([, v]) => v != null && v !== '')
                  .map(([k, v]) => (
                    <span key={k} className="block">
                      <span className="text-[var(--text-muted)]">{k}:</span>{' '}
                      <span className="font-mono break-all">{String(v)}</span>
                    </span>
                  ))}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mt-2 block text-[10px] text-[var(--text-muted)] hover:underline"
            >
              {t('common.close')}
            </button>
          </span>
        )}
      </span>
    </NodeViewWrapper>
  );
}
